import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<AuthenticationView />` — Smart Health Account sign-in
 * (auth + KEK release in active session).
 *
 * Per Tech Spec §6.2.3 + PFO §2.2 + scope memo §5.1:
 *
 *   knowledge factor + possession factor → PMK derivation →
 *   wrapped KEK fetch → KEK unwrap → ActiveSession.loadUnwrappedKek
 *
 * Drives the `IAuthenticationFlow` orchestrator + dispatches step
 * views by current state.
 *
 * **Doctrinal load-bearing posture (encoded structurally):**
 *
 *   - Both factors required (PFO §2.2 standard PHG access:
 *     "Knowledge + possession"). Step machine cannot skip
 *     possession factor.
 *   - PMK + KEK are non-extractable CryptoKey objects after Web
 *     Crypto handoff; the runtime structurally refuses to surface
 *     bytes. NO SHT BACKDOOR is enforced at the runtime layer,
 *     not engineering discipline.
 *   - Sign-out is patient-affirmative; idle timeout per PFO §2.2
 *     12-24h configurable lands as v1.5+ refinement.
 *
 * **Patient-Facing Terms v1.3 doctrinal-copy gate** (coord issue
 * #42). Sign-in copy is placeholder until v1.3 lands; views
 * structured for mechanical swap.
 */
import * as React from 'react';
import { useLocale } from '../i18n/locale-context.js';
import { AuthIntroStep } from './_authentication/intro-step.js';
import { KnowledgeFactorStep } from './_authentication/knowledge-factor-step.js';
import { PossessionFactorStep } from './_authentication/possession-factor-step.js';
import { AuthPendingStep } from './_authentication/pending-step.js';
import { SessionActiveStep } from './_authentication/session-active-step.js';
import { FailedOrSignedOutStep } from './_authentication/failed-or-signed-out-step.js';
function useAuthFlowState(flow) {
    const [state, setState] = React.useState(() => flow.getState());
    React.useEffect(() => {
        return flow.subscribe(setState);
    }, [flow]);
    return state;
}
export function AuthenticationView(props) {
    const state = useAuthFlowState(props.flow);
    const { t } = useLocale();
    return (_jsxs("section", { "aria-labelledby": "auth-heading", children: [_jsx("h2", { id: "auth-heading", children: t('authentication.heading') }), renderStep(state, props.flow, t)] }));
}
function renderStep(state, flow, t) {
    switch (state.kind) {
        case 'intro':
            return _jsx(AuthIntroStep, { onBegin: () => flow.beginSignIn() });
        case 'collecting-knowledge-factor':
            return (_jsx(KnowledgeFactorStep, { onSubmit: (factor) => flow.submitKnowledgeFactor(factor), onCancel: () => flow.signOut() }));
        case 'collecting-possession-factor':
            return (_jsx(PossessionFactorStep, { onSubmit: (code) => {
                    void flow.submitPossessionFactor(code);
                }, onCancel: () => flow.signOut() }));
        case 'deriving-pmk':
            return _jsx(AuthPendingStep, { label: t('authentication.pending.label.deriving_pmk') });
        case 'fetching-wrapped-kek':
            return _jsx(AuthPendingStep, { label: t('authentication.pending.label.fetching_wrapped_kek') });
        case 'unwrapping-kek':
            return _jsx(AuthPendingStep, { label: t('authentication.pending.label.unwrapping_kek') });
        case 'session-active':
            return (_jsx(SessionActiveStep, { handle: state.handle, onSignOut: () => flow.signOut() }));
        case 'signed-out':
            return (_jsx(FailedOrSignedOutStep, { kind: "signed-out", onRestart: () => flow.restart() }));
        case 'failed':
            return (_jsx(FailedOrSignedOutStep, { kind: "failed", detail: `${state.code}: ${state.message}`, onRestart: () => flow.restart() }));
    }
}
//# sourceMappingURL=authentication-view.js.map